import { forwardRef, useRef } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';
import { Field, ErrorMessage, FieldProps } from 'formik';
import type { InputRef } from 'antd';
import { Input, Flex } from 'antd';
import TextError from 'components/TextError';
import FieldLabel from 'components/FieldLabel';
import { PasswordProps } from 'utils/globalTypes';
import HideIcon from 'components/HideIcon';
import RevealIcon from 'components/RevealIcon';
import If from 'components/If';
import {
  QaPasswordManagerDetailsPage,
  QaUpdatePasswordModal,
} from 'testdata/constants';

function iconRender(visible: boolean, tooltip: string) {
  return visible ? (
    <HideIcon
      className='eyeIcon'
      tooltip={tooltip}
      data-qa-selector={QaPasswordManagerDetailsPage.buttonHidePassword}
    />
  ) : (
    <RevealIcon
      className='eyeIcon'
      tooltip={tooltip}
      data-qa-selector={QaPasswordManagerDetailsPage.buttonRevealPassword}
    />
  );
}

const Password = forwardRef<InputRef, PasswordProps>((props, ref) => {
  const {
    label,
    name,
    qaSelector,
    showErrMsg = true,
    info,
    tooltip = 'password',
    ...rest
  } = props;

  const handleIconRender = (visible: boolean) => iconRender(visible, tooltip);
  const hasUserEditedRef = useRef(false);

  return (
    <Flex vertical align='flex-start' className='formFieldContainer'>
      <FieldLabel label={label} info={info} />
      <Field name={name}>
        {({ field, meta, form }: FieldProps) => {
          const { setFieldValue } = form;
          const fieldStatus = meta.touched && meta.error ? 'error' : '';
          const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            hasUserEditedRef.current = true;
            (rest.onChange ?? field.onChange)(e);
          };
          const onBlur = (e: FocusEvent<HTMLInputElement>) => {
            // See components/Input for why: auto-focused + never-edited +
            // empty must not trigger validation, or same-form nav controls
            // get shifted out from under an in-flight click.
            if (!hasUserEditedRef.current && e.target.value.trim() === '') {
              return;
            }
            setFieldValue(name, e.target.value.trim()).then(() => {
              field.onBlur(e);
            });
          };

          return (
            <Input.Password
              status={fieldStatus}
              id={name}
              iconRender={handleIconRender}
              ref={ref}
              {...field}
              {...rest}
              onChange={handleChange}
              onBlur={onBlur}
              data-qa-selector={qaSelector}
            />
          );
        }}
      </Field>
      <If condition={showErrMsg}>
        <ErrorMessage name={name}>
          {(msg) => (
            <TextError
              msg={msg}
              qaSelector={`${QaUpdatePasswordModal.alertInputErrorMessage}${name}`}
            />
          )}
        </ErrorMessage>
      </If>
    </Flex>
  );
});

export default Password;
